// Contatos: lista em tempo real, ficha, criar/editar, excluir e matrícula em minicurso.
import React, { useEffect, useMemo, useState } from 'react';
import Modal from 'react-modal';
import { FiEdit2, FiEye, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { collection, deleteDoc, doc, onSnapshot, query, where } from 'firebase/firestore';
import { firestore } from '../config/firebaseConfig';
import Carregando from '../components/Carregando';
import ImportContactsModal from '../components/ImportContactsModal';
import ContatoFicha, { SeloContato } from '../components/contatos/ContatoFicha';
import ContatoFormModal from '../components/contatos/ContatoFormModal';
import { STATUS_CONTATO, cursoAtualDoContato, formatarDataSimples, formatarTelefoneContato } from '../components/contatos/contatosUtil';
import { useBaseRelatorio } from '../components/relatorios/useRelatorios';
import { semAcento } from '../components/relatorios/util';
import '../components/contatos/Contatos.css';

Modal.setAppElement('#root');

const ORDENACOES = {
  recentes: { rotulo: 'Mais recentes', comparar: (a, b) => String(b.dataCriacao || '').localeCompare(String(a.dataCriacao || '')) },
  nome: { rotulo: 'Nome (A–Z)', comparar: (a, b) => String(a.nome || '').localeCompare(String(b.nome || ''), 'pt-BR') },
};

function Contatos() {
  const tenantId = localStorage.getItem('tenantId');
  const [contatos, setContatos] = useState(null);
  const [erro, setErro] = useState('');
  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');
  const [ordenacao, setOrdenacao] = useState('recentes');
  const [fichaId, setFichaId] = useState(null);
  const [editando, setEditando] = useState(null); // null = fechado; {} = novo; contato = edição
  const [excluindo, setExcluindo] = useState(null);
  const [importando, setImportando] = useState(false);
  const { minicursos, progressos } = useBaseRelatorio(tenantId);

  useEffect(() => {
    if (!tenantId) return undefined;
    return onSnapshot(
      query(collection(firestore, 'contatos'), where('tenantId', '==', tenantId)),
      (s) => setContatos(s.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => {
        console.error('Contatos:', e);
        setErro('Não foi possível carregar os contatos.');
      },
    );
  }, [tenantId]);

  const visiveis = useMemo(() => {
    if (!contatos) return [];
    const termo = semAcento(busca.trim());
    const digitos = termo.replace(/\D/g, '');
    return contatos
      .filter((c) => !filtroStatus || c.status === filtroStatus)
      .filter((c) => !termo
        || semAcento(c.nome).includes(termo)
        || semAcento(c.email).includes(termo)
        || (digitos.length >= 3 && String(c.telefone || '').includes(digitos)))
      .sort(ORDENACOES[ordenacao].comparar);
  }, [contatos, busca, filtroStatus, ordenacao]);

  const nomeDoCurso = (id) => (minicursos || []).find((m) => m.id === id)?.nome || 'Curso removido';
  const fichaAberta = fichaId && contatos ? contatos.find((c) => c.id === fichaId) : null;

  const excluir = async () => {
    try {
      await deleteDoc(doc(firestore, 'contatos', excluindo.id));
      toast.success(`${excluindo.nome || 'Contato'} excluído.`);
      if (fichaId === excluindo.id) setFichaId(null);
    } catch (e) {
      console.error('Erro ao excluir contato:', e);
      toast.error('Não foi possível excluir o contato.');
    } finally {
      setExcluindo(null);
    }
  };

  if (!tenantId) return <p className="qz-empty">Não foi possível identificar a empresa. Saia e entre de novo.</p>;

  return (
    <div className="contatos-container">
      {/* Cabeçalho no visual original (pages/Contatos.css): título, botões azuis e busca */}
      <div className="header-container">
        <h1>Gerenciador de Contatos</h1>
        <div className="action-buttons">
          <button onClick={() => setEditando({})} className="add-button">
            Adicionar Contato
          </button>
          <button onClick={() => setImportando(true)} className="import-button">
            Importar Contatos
          </button>
        </div>
        <input
          type="text"
          placeholder="Buscar por nome, telefone ou e-mail..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="search-input"
          aria-label="Buscar contatos"
          autoComplete="off"
        />
      </div>

      <div className="ct-filtros">
        <select aria-label="Filtrar por status" value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)}>
          <option value="">Todos os status</option>
          {STATUS_CONTATO.map((s) => <option key={s.valor} value={s.valor}>{s.rotulo}</option>)}
        </select>
        <select aria-label="Ordenar" value={ordenacao} onChange={(e) => setOrdenacao(e.target.value)}>
          {Object.entries(ORDENACOES).map(([chave, o]) => <option key={chave} value={chave}>{o.rotulo}</option>)}
        </select>
      </div>

      {erro && <p className="qz-empty">{erro}</p>}
      {!erro && !contatos && <Carregando texto="Carregando contatos..." />}
      {!erro && contatos && visiveis.length === 0 && (
        <div className="qz-empty">
          {contatos.length === 0 ? 'Nenhum contato cadastrado ainda.' : 'Nenhum contato corresponde aos filtros.'}
        </div>
      )}

      {!erro && visiveis.length > 0 && (
        <div className="contato-list-container">
          <div className="ct-tabela-rolagem">
            <table className="contato-table">
              <thead>
                <tr>
                  <th scope="col">Nome</th>
                  <th scope="col">Telefone</th>
                  <th scope="col">Status</th>
                  <th scope="col">Minicurso em andamento</th>
                  <th scope="col">Cadastro</th>
                  <th scope="col">Ações</th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((c) => {
                  const atual = progressos ? cursoAtualDoContato(c, progressos) : null;
                  return (
                    <tr key={c.id}>
                      <td>
                        <button type="button" className="ct-link-nome" onClick={() => setFichaId(c.id)}>{c.nome || 'Sem nome'}</button>
                        {c.email && <span className="ct-suave ct-bloco">{c.email}</span>}
                      </td>
                      <td className="ct-sem-quebra">{formatarTelefoneContato(c.telefone)}</td>
                      <td><SeloContato status={c.status} /></td>
                      <td>
                        {atual
                          ? <>{nomeDoCurso(atual.minicursoId)} <span className="ct-suave">· etapa {atual.ultimaEtapaConcluida || 0}</span></>
                          : <span className="ct-suave">—</span>}
                      </td>
                      <td className="ct-suave ct-sem-quebra">{formatarDataSimples(c.dataCriacao)}</td>
                      <td>
                        <div className="ct-acoes-linha">
                          <button type="button" className="btn-ghost ct-icone-botao" onClick={() => setFichaId(c.id)} aria-label={`Ver ficha de ${c.nome}`} title="Ver ficha">
                            <FiEye aria-hidden="true" />
                          </button>
                          <button type="button" className="btn-ghost ct-icone-botao" onClick={() => setEditando(c)} aria-label={`Editar ${c.nome}`} title="Editar">
                            <FiEdit2 aria-hidden="true" />
                          </button>
                          <button type="button" className="btn-ghost ct-icone-botao ct-icone-botao--perigo" onClick={() => setExcluindo(c)} aria-label={`Excluir ${c.nome}`} title="Excluir">
                            <FiTrash2 aria-hidden="true" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {fichaAberta && minicursos && progressos && (
        <ContatoFicha
          tenantId={tenantId}
          contato={fichaAberta}
          minicursos={minicursos}
          progressos={progressos}
          onFechar={() => setFichaId(null)}
          onEditar={() => setEditando(fichaAberta)}
          onExcluir={() => setExcluindo(fichaAberta)}
        />
      )}

      {editando && (
        <ContatoFormModal tenantId={tenantId} contato={editando.id ? editando : null} onFechar={() => setEditando(null)} />
      )}

      {excluindo && (
        <Modal isOpen onRequestClose={() => setExcluindo(null)} className="ct-modal ct-modal--pequeno" overlayClassName="ct-overlay" contentLabel="Excluir contato">
          <h2 className="ct-modal__titulo">Excluir {excluindo.nome || 'contato'}?</h2>
          <p className="ct-suave">O cadastro sai da lista. O histórico de minicursos e respostas continua nos relatórios.</p>
          <footer className="ct-modal__rodape">
            <button type="button" className="btn-secondary ct-botao" onClick={() => setExcluindo(null)}>Cancelar</button>
            <button type="button" className="btn-danger ct-botao" onClick={excluir}>Excluir</button>
          </footer>
        </Modal>
      )}

      {importando && <ImportContactsModal onClose={() => setImportando(false)} />}
    </div>
  );
}

export default Contatos;
